import { PrimaryButton, SecondaryButton, TextButton } from '../components/Button';
import { Card } from '../components/Card';
import { Screen } from '../components/Screen';
import { StatusRing } from '../components/Status';
import { copy } from '../copy';
import { hasCoordinates, navigationHref, openExternal, telHref } from '../lib/links';
import { shortParcelLabel } from '../lib/parcel';
import { formatClock } from '../lib/time';
import { findStopById, stopCounts } from '../state/route';
import { selectCurrentStop, useAppStore } from '../state/store';

/** E2/E3/E4 — Stopdetail. Twee pakketten op één adres zijn één bezoek met twee rijen. */
export function StopScreen() {
  const route = useAppStore((s) => s.route);
  const stopId = useAppStore((s) => s.stopId);
  const current = useAppStore(selectCurrentStop);
  const navigate = useAppStore((s) => s.navigate);
  const setScanPurpose = useAppStore((s) => s.setScanPurpose);
  const recordAction = useAppStore((s) => s.recordAction);

  const stop = route ? findStopById(route, stopId) : null;
  if (!route || !stop) return null;

  const counts = stopCounts(route);
  const arrived = stop.arrivedAt !== null;
  const pending = stop.parcels.filter((parcel) => parcel.status !== 'delivered');
  const multi = stop.parcels.length > 1;
  const isNext = current?.id === stop.id;

  const destination = {
    lat: stop.lat,
    lng: stop.lng,
    label: stop.partnerName,
    addressLine: stop.street,
    postalCodeCity: stop.postalCodeCity,
  };

  const openScanner = () => {
    setScanPurpose('deliver');
    navigate('scanner');
  };

  const arrive = () => {
    void recordAction({
      type: 'stop.arrive',
      request: { stopId: stop.id },
      subject: stop.partnerName,
    });
  };

  const scanLabel =
    stop.parcels.length > 1 && pending.length === 1 && stop.parcels.length !== pending.length
      ? copy.stop.deliverSecond
      : copy.stop.deliver;

  const foot = arrived ? (
    <>
      <PrimaryButton onClick={openScanner}>{scanLabel}</PrimaryButton>
      <div className="row row--between" style={{ padding: '0 4px' }}>
        {stop.phone ? (
          <TextButton onClick={() => openExternal(telHref(stop.phone!))}>
            {copy.stop.call}
          </TextButton>
        ) : (
          <span className="t-caption o-55">{copy.stop.noPhone}</span>
        )}
        <TextButton variant="muted" onClick={() => navigate('fail')}>
          {copy.stop.fail}
        </TextButton>
      </div>
    </>
  ) : (
    <>
      <div className="row">
        <div style={{ flex: 1 }}>
          <SecondaryButton onClick={() => openExternal(navigationHref(destination))}>
            {copy.stop.navigate}
          </SecondaryButton>
        </div>
        <div style={{ flex: 1 }}>
          <SecondaryButton
            disabledReason={stop.phone ? null : copy.stop.noPhone}
            onClick={() => openExternal(telHref(stop.phone!))}
          >
            {copy.stop.call}
          </SecondaryButton>
        </div>
      </div>
      <PrimaryButton onClick={arrive}>{copy.stop.arrive}</PrimaryButton>
    </>
  );

  return (
    <Screen name="stop" foot={foot}>
      <div className="pad-x" style={{ paddingTop: 12 }}>
        <TextButton onClick={() => navigate('ride')}>{copy.stop.back}</TextButton>
      </div>

      <div className="pad-x" style={{ paddingTop: 14 }}>
        <div className="row" style={{ gap: 12, alignItems: 'center' }}>
          <StatusRing
            state={arrived ? 'arrived' : 'current'}
            size={38}
            label={stop.stopOrder}
            ariaLabel={
              arrived
                ? `stop ${stop.stopOrder}, aangekomen`
                : `stop ${stop.stopOrder}, nu naartoe`
            }
          />
          {arrived ? (
            <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--color-mint)' }}>
              {copy.stop.arrived(formatClock(stop.arrivedAt))}
            </span>
          ) : (
            <span className="o-65" style={{ fontSize: 16 }}>
              {copy.stop.header(stop.stopOrder, counts.total, formatClock(stop.plannedArrivalAt))}
            </span>
          )}
        </div>

        {!isNext ? (
          <p className="t-caption o-65" style={{ marginTop: 10, marginBottom: 0 }}>
            {copy.ride.notNext}
          </p>
        ) : null}

        {!hasCoordinates(destination) ? (
          <p className="t-caption o-55" style={{ marginTop: 10, marginBottom: 0 }}>
            {copy.stop.noCoordinates}
          </p>
        ) : null}

        <div style={{ fontSize: 24, fontWeight: 600, marginTop: 16 }}>{stop.partnerName}</div>
        <div style={{ fontSize: 21, marginTop: 6 }}>
          {stop.street}
          <br />
          {stop.postalCodeCity}
        </div>
      </div>

      <div className="stack stack--sm pad-x" style={{ paddingTop: 16 }}>
        {multi ? (
          <Card variant="row">
            <div className="row" style={{ gap: 10, alignItems: 'center' }}>
              <span className="badge-square" aria-hidden>
                {stop.parcels.length}
              </span>
              <span style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.4 }}>
                {copy.stop.twoParcels}
              </span>
            </div>
          </Card>
        ) : null}

        {stop.parcels.map((parcel) => {
          const delivered = parcel.status === 'delivered';
          return (
            <div
              key={parcel.parcelNumber}
              className={`listrow${delivered ? ' card--success' : ''}`}
              style={{ padding: '13px 16px' }}
            >
              <div>
                <div className="listrow__mono">{shortParcelLabel(parcel.parcelNumber)}</div>
                <div
                  style={{
                    fontSize: 14,
                    marginTop: 2,
                    color: delivered ? 'var(--color-mint)' : 'var(--text-65)',
                  }}
                >
                  {delivered ? copy.stop.parcelDelivered : copy.stop.parcelPending}
                </div>
              </div>
              <StatusRing
                state={delivered ? 'delivered' : 'open'}
                size={26}
                ariaLabel={delivered ? 'bezorgd' : 'nog afmelden'}
              />
            </div>
          );
        })}
      </div>
    </Screen>
  );
}
